import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { Card, CircularProgress, useTheme } from "@mui/material";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import ScanInformation from "../myScan/ScanInformation";
import ScanDetails from "../myScan/ScanDetails/ScanDetails";
import Discovery from "../../discovery/Discovery";
import AwsInventory from "../../cloud/AwsInventory";
import { setEdit } from "../myScan/MyScanTable_Details_slice";
import "./DiscoverySecurityPage.css";

type ActiveTab = "security" | "discovery";

const DiscoverySecurityPage: React.FC = () => {
  const { scan_uuid } = useParams<{ scan_uuid: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Read state from Redux (set during row-click in ScansV2Page)
  const { status: reduxStatus, onprem_posture_id, scanType } = useSelector(
    (state: RootState) => state.MyScanDetails
  );

  const locationScanType = String(
    (location.state as { scanType?: string } | null)?.scanType || ""
  ).toLowerCase();
  const queryScanType = String(searchParams.get("scan_type") || "").toLowerCase();

  // Exact match only — must not treat onprem discovery-posture or aws_discovery as this path
  const isAwsDiscoveryPosture = useMemo(() => {
    const candidates = [
      String(scanType || "").toLowerCase(),
      locationScanType,
      queryScanType,
    ];
    return candidates.some((t) => t === "aws_discovery_posture");
  }, [scanType, locationScanType, queryScanType]);

  const activeTabFromQuery = searchParams.get("tab") === "discovery" ? "discovery" : "security";
  const [activeTab, setActiveTab] = useState<ActiveTab>(activeTabFromQuery);
  const [scanStatus, setScanStatus] = useState<string>(reduxStatus || "");
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(!reduxStatus);
  const scanSummaryId = onprem_posture_id || scan_uuid;

  // Determine if scan is in progress
  const isInProgress = useCallback((status: string): boolean => {
    const s = String(status ?? "").toLowerCase().trim();
    return (
      s === "in progress" ||
      s === "processing" ||
      s === "queue" ||
      s === "1" ||
      s === "2"
    );
  }, []);

  // Fetch scan status from API to get the latest state
  useEffect(() => {
    const fetchScanStatus = async () => {
      if (!scan_uuid) return;
      try {
        setIsLoadingStatus(true);
        const payloadUuid = onprem_posture_id || scan_uuid;
        const response = await axiosInstance.post(END_POINTS.MY_SCAN_DETAILS, {
          scan_uuid: payloadUuid,
        });
        const data = response?.data?.data;
        if (data?.scan_details && Array.isArray(data.scan_details)) {
          // Check if any detail is still processing
          const anyProcessing = data.scan_details.some((d: any) => {
            const s = Number(d.status);
            return s === 1 || s === 2;
          });
          const derivedStatus = anyProcessing ? "In Progress" : "Completed";
          setScanStatus(derivedStatus);
        } else {
          // Fallback to the redux status
          setScanStatus(reduxStatus || "Completed");
        }
      } catch (error) {
        console.error("[DiscoverySecurityPage] Error fetching scan status:", error);
        setScanStatus(reduxStatus || "Completed");
      } finally {
        setIsLoadingStatus(false);
      }
    };

    fetchScanStatus();
  }, [scan_uuid, onprem_posture_id, reduxStatus]);

  const scanIsInProgress = isInProgress(scanStatus);

  // When scan is in progress, disable Discovery tab
  const isDiscoveryDisabled = scanIsInProgress;

  const handleTabChange = (tab: ActiveTab) => {
    if (tab === "discovery" && isDiscoveryDisabled) return;
    setActiveTab(tab);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", tab);
    if (isAwsDiscoveryPosture && scan_uuid) {
      nextParams.set("scan_id", scan_uuid);
      nextParams.set("scan_type", "aws_discovery_posture");
    }
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    setActiveTab(activeTabFromQuery);
  }, [activeTabFromQuery]);

  // Keep scan_id + scan_type in URL so Security uses module=cloud and Discovery uses AWS Inventory
  useEffect(() => {
    if (!isAwsDiscoveryPosture || !scan_uuid) return;
    const nextParams = new URLSearchParams(searchParams);
    let changed = false;
    if (nextParams.get("scan_id") !== scan_uuid) {
      nextParams.set("scan_id", scan_uuid);
      changed = true;
    }
    if (nextParams.get("scan_type") !== "aws_discovery_posture") {
      nextParams.set("scan_type", "aws_discovery_posture");
      changed = true;
    }
    if (changed) {
      setSearchParams(nextParams, { replace: true });
    }
    // Ensure ScanDetails / children see aws type in Redux even after refresh
    if (String(scanType || "").toLowerCase() !== "aws_discovery_posture") {
      dispatch(
        setEdit({
          edit: true,
          scan_uuid,
          status: reduxStatus || "",
          scanTime: "",
          scanType: "aws_discovery_posture",
          onprem_posture_id: onprem_posture_id,
        }),
      );
    }
  }, [
    isAwsDiscoveryPosture,
    scan_uuid,
    searchParams,
    setSearchParams,
    dispatch,
    scanType,
    reduxStatus,
    onprem_posture_id,
  ]);

  if (isLoadingStatus) {
    return (
      <Card
        variant="outlined"
        sx={{ height: "calc(100vh - 120px)", borderRadius: 3 }}
      >
        <div className="ds-loading">
          <CircularProgress size={36} sx={{ color: "#3A2ADA" }} />
        </div>
      </Card>
    );
  }

  return (
    <div className={`ds-page ${isDarkMode ? "dark-mode" : ""}`}>
      {/* Toggle Bar */}
      <div className="ds-header">
        <div className="ds-header-left">
          <div className="ds-toggle-bar">
            <button
              className={`ds-toggle-btn ${activeTab === "security" ? "ds-toggle-btn--active" : ""}`}
              onClick={() => handleTabChange("security")}
              id="ds-tab-security"
            >
              Security
            </button>
            <button
              className={`ds-toggle-btn ${activeTab === "discovery" ? "ds-toggle-btn--active" : ""}`}
              onClick={() => handleTabChange("discovery")}
              disabled={isDiscoveryDisabled}
              id="ds-tab-discovery"
              title={
                isDiscoveryDisabled
                  ? "Discovery is available after the scan completes"
                  : ""
              }
            >
              Discovery
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="ds-content">
        {activeTab === "security" && (
          <>
            {scanIsInProgress ? (
              /* In-progress: show the scan-status polling view (ScanInformation) */
              <ScanInformation />
            ) : (
              /* Completed: cloud posture for aws_discovery_posture; on-prem posture otherwise */
              <ScanDetails
                scanSummaryId={isAwsDiscoveryPosture ? scan_uuid : scanSummaryId}
                moduleOverride={isAwsDiscoveryPosture ? "cloud" : undefined}
              />
            )}
          </>
        )}

        {activeTab === "discovery" && !isDiscoveryDisabled && (
          isAwsDiscoveryPosture ? (
            /* AWS Inventory scoped to this scan_id (query param set above) */
            <AwsInventory />
          ) : (
            <Discovery scanId={scan_uuid} />
          )
        )}
      </div>
    </div>
  );
};

export default DiscoverySecurityPage;
